import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
import { Lock, Save, Loader2 } from 'lucide-react';

interface SaveSessionButtonProps {
  sessionId: string;
  isSaved: boolean;
  hasBackgroundMusic: boolean;
  onSaved?: () => void;
  className?: string;
}

export function SaveSessionButton({
  sessionId,
  isSaved,
  hasBackgroundMusic,
  onSaved,
  className = ''
}: SaveSessionButtonProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/save`, {}, {
        includeAuth: true
      });

      if (response.success) {
        toast({
          title: 'Session Saved',
          description: hasBackgroundMusic
            ? 'Your background music settings are now locked and pre-mixed audio has been created.'
            : 'Your session has been saved.',
        });
        onSaved?.();
      }
    } catch (error: any) {
      console.error('Failed to save session:', error);
      toast({
        title: 'Failed to Save',
        description: error.message || 'Could not save session. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
      setDialogOpen(false);
    }
  };

  if (isSaved) {
    return (
      <Badge variant="secondary" className={`text-xs ${className}`}>
        <Lock className="h-3 w-3 mr-1" />
        Saved
      </Badge>
    );
  }

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={className}
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Session
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Save Session?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              This will permanently lock your background music settings.
            </p>
            {hasBackgroundMusic && (
              <p className="text-amber-600 dark:text-amber-400">
                Pre-mixed audio will be created with your current volume settings.
                You won't be able to change the background music or volume after saving.
              </p>
            )}
            <p className="font-medium">
              This action cannot be undone.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSave}
            disabled={isSaving}
            className="bg-primary"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Save & Lock
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
